import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const InvoiceAgingChart = ({ data: liveData }) => {
  const getCategoryColor = (label) => {
    switch (label) {
      case 'Current': return '#10B981';
      case '1-30 Days': return '#3B82F6';
      case '31-60 Days': return '#F59E0B';
      case '61-90 Days': return '#DC2626';
      case '90+ Days': return '#991B1B';
      default: return '#64748B';
    }
  };

  const mockAgingData = [
    { range: 'Current', amount: 1250000, color: '#10B981' },
    { range: '1-30 Days', amount: 850000, color: '#3B82F6' },
    { range: '31-60 Days', amount: 420000, color: '#F59E0B' },
    { range: '61-90 Days', amount: 180000, color: '#DC2626' }
  ];

  const processedData = (liveData && liveData.length > 0) 
    ? liveData.map(item => ({ ...item, color: getCategoryColor(item.range) }))
    : mockAgingData;

  const agingData = processedData;

  const totalAmount = agingData.reduce((sum, item) => sum + item.amount, 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              Amount: <span className="font-semibold text-foreground">₹{(data.amount / 100000).toFixed(1)}L</span>
            </p>
            <p className="text-sm text-muted-foreground">
              Invoices: <span className="font-semibold text-foreground">{data.count}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              Percentage: <span className="font-semibold text-foreground">{((data.amount / totalAmount) * 100).toFixed(1)}%</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Invoice Aging</h3>
          <p className="text-sm text-muted-foreground">Outstanding receivables by aging period</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Total Outstanding</p>
          <p className="text-lg font-semibold text-foreground">₹{(totalAmount / 100000).toFixed(1)}L</p>
        </div>
      </div>
      
      <div className="w-full h-80" aria-label="Invoice Aging Bar Chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={agingData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis 
              dataKey="range" 
              tick={{ fontSize: 12, fill: '#64748B' }}
              axisLine={{ stroke: '#E2E8F0' }}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#64748B' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickFormatter={(value) => `₹${(value / 100000).toFixed(1)}L`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
              {agingData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {agingData.map((item, index) => (
          <div key={index} className="text-center p-3 bg-muted rounded-lg">
            <div 
              className="w-4 h-4 rounded mx-auto mb-2"
              style={{ backgroundColor: item.color }}
            />
            <p className="text-xs text-muted-foreground mb-1">{item.range}</p>
            <p className="text-sm font-semibold text-foreground">₹{(item.amount / 100000).toFixed(1)}L</p>
            {item.count !== undefined && <p className="text-xs text-muted-foreground">{item.count} invoices</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InvoiceAgingChart;